import { useState } from 'react';

interface SourceColumn {
  id: string;
  label: string;
}


interface UseAutoMappingProps<T extends Record<string, any>> {
  sourceData: any[][];
  originalSourceData: any[][];
  mapping: T;
  setMapping: (mapping: T) => void;
  setSourceColumns: (columns: SourceColumn[]) => void;
  setSuccess: (message: string) => void;
  setSkipLines: (lines: number) => void;
  onSourceDataUpdate?: (newData: any[][]) => void;
  columnMappings: { [key: string]: string[] };
  setSourceDataHeaderRow?: (row: any[]) => void;
}

export const useAutoMapping = <T extends Record<string, any>>({
  sourceData,
  originalSourceData,
  mapping,
  setMapping,
  setSourceColumns,
  setSuccess,
  setSkipLines,
  onSourceDataUpdate,
  columnMappings,
  setSourceDataHeaderRow,
}: UseAutoMappingProps<T>) => {
  const [autoMappingDone, setAutoMappingDone] = useState(false);

  const handleAutoMapFirstRow = () => {
    if (originalSourceData.length > 0) {
      const firstRow = originalSourceData[0];
      if (setSourceDataHeaderRow) {
        setSourceDataHeaderRow(firstRow);
      }
      const newMapping: T = { ...mapping };
      
      // Update sourceColumns with first row data
      const newSourceColumns = firstRow.map((header, index) => ({
        id: `col${index}`,
        label: header?.toString() || `Column ${index + 1}`
      }));
      setSourceColumns(newSourceColumns);
      
      // For each field in mapping
      Object.keys(mapping).forEach((field) => {
        // Get possible column names for this field
        const possibleNames = columnMappings[field] || [field];
        
        // Find the best match in the first row
        let bestMatch = -1;
        let bestScore = 0;
        
        firstRow.forEach((header, colIndex) => {
          if (header) {
            const headerLower = header.toString().toLowerCase().trim();
            possibleNames.forEach(name => {
              const nameLower = name.toLowerCase();
              // Check for exact match
              if (headerLower === nameLower) {
                bestMatch = colIndex;
                bestScore = 1;
              }
              // Check for contains with word boundary
              else if (new RegExp(`\\b${nameLower}\\b`).test(headerLower) && bestScore < 0.8) {
                bestMatch = colIndex;
                bestScore = 0.8;
              }
              // Check for similar words with word boundary
            //   else if (headerLower.split(/\s+/).some(word => 
            //     nameLower.split(/\s+/).some(n => 
            //       new RegExp(`\\b${n}\\b`).test(word) || new RegExp(`\\b${word}\\b`).test(n)
            //     )
            //   ) && bestScore < 0.7) {
            //     bestMatch = colIndex;
            //     bestScore = 0.7;
            //   }
            });
          }
        });

        // If we found a match, update the mapping
        if (bestMatch !== -1) {
          newMapping[field as keyof T] = `col${bestMatch}` as T[keyof T];
        }
      });
      
      setMapping(newMapping);
      setAutoMappingDone(true);
      setSuccess("Successfully mapped columns based on first row");

      // Remove first row from sourceData
      if (onSourceDataUpdate) {
        const newSourceData = originalSourceData.slice(1);
        onSourceDataUpdate(newSourceData);
      }
    }
  };

  return {
    handleAutoMapFirstRow,
    autoMappingDone,
    setAutoMappingDone
  };
}; 